import { useEffect, useMemo, useRef, useState } from "haunted";
import Immutable, { isCollection } from "immutable";
import { SimpleActionCreator } from "redux-act";
import { REDUCER_NAME, ReduxContext, StateMap, reducerActions } from "./ReduxContext";
import { NestedKeyOf, PickByNestedKeyOf } from "./ReducerTypes";

export function useReduxState<PropertyName extends NestedKeyOf<StateMap>>(
	propertyName: PropertyName
): [PickByNestedKeyOf<StateMap, PropertyName>, (val: PickByNestedKeyOf<StateMap, PropertyName>) => void] {
	const readRaw = () => ReduxContext.store.getState().getIn([REDUCER_NAME, ...propertyName.split(".")]);

	const rawRef = useRef(readRaw());

	const [, setVersion] = useState<number>(0);

	const toJsIfNeeded = (val: any) => (isCollection(val) ? (val as Immutable.Collection<any, any>).toJS() : val);

	const data = useMemo(() => toJsIfNeeded(rawRef.current), [rawRef.current]);

	const dispatch = (val: PickByNestedKeyOf<StateMap, PropertyName>) => {
		const match = reducerActions.find((it) => it.propertyName === propertyName);
		if (!match || !match.action) {
			throw new Error(`No action mapped for property "${String(propertyName)}"`);
		}
		const action = match.action as unknown as SimpleActionCreator<typeof val>;
		ReduxContext.dispatch(action(val));
	};

	useEffect(() => {
		let isScheduled = false;

		const syncIfChanged = () => {
			const nextRaw = readRaw();
			if (nextRaw !== rawRef.current) {
				rawRef.current = nextRaw;
				if (!isScheduled) {
					isScheduled = true;
					queueMicrotask(() => {
						isScheduled = false;
						setVersion((x) => x + 1);
					});
				}
			}
		};

		syncIfChanged();

		return ReduxContext.store.subscribe(syncIfChanged);
	}, [propertyName]);

	return [data, dispatch];
}
